/**
 * Utility functions for product operations
 */

/**
 * Get the price for a specific variant combination
 * @param {Object} product - The product object
 * @param {Object} selectedVariants - Object containing selected variant options, e.g. { "Size": "Large", "Color": "Red" }
 * @returns {Number} - The price for the selected variant combination, or the base product price if no variant-specific price exists
 */
export function getVariantPrice(product, selectedVariants = {}) {
  // If no product or product is missing required data, return 0
  if (!product) {
    return 0;
  }

  // If no variants are selected, return the base price
  if (!selectedVariants || Object.keys(selectedVariants).length === 0) {
    return product.price || 0;
  }

  // Check for undefined variant values and remove them
  const cleanedVariants = {};
  for (const [key, value] of Object.entries(selectedVariants)) {
    if (value !== undefined && value !== null) {
      cleanedVariants[key] = value;
    }
  }

  // If all selected variants were undefined, return base price
  if (Object.keys(cleanedVariants).length === 0) {
    return product.price || 0;
  }

  // Use cleaned variants for price calculation
  selectedVariants = cleanedVariants;

  // If product has variants, try to find a matching variant
  if (product.variants && Array.isArray(product.variants)) {
    // First try to match by Operating System if it exists
    if (selectedVariants['Operating System']) {
      const osVariant = product.variants.find(v => 
        v.options && v.options.toLowerCase() === selectedVariants['Operating System'].toLowerCase()
      );
      if (osVariant && osVariant.price) {
        console.log(`Found OS variant match: ${osVariant.options} with price: ${osVariant.price}`);
        return osVariant.price;
      }
    }

    // Then try to match by name and options
    const matchingVariant = product.variants.find(variant => {
      if (!variant.name || !variant.options) return false;
      
      // Try to match by name and options
      const variantName = variant.name.toLowerCase();
      const variantOption = variant.options.toLowerCase();
      
      // Check if any selected variant matches this variant's name and option
      return Object.entries(selectedVariants).some(([key, value]) => {
        const selectedKey = key.toLowerCase();
        const selectedValue = value.toLowerCase();
        return variantName === selectedKey && variantOption === selectedValue;
      });
    });

    if (matchingVariant && matchingVariant.price) {
      console.log(`Found matching variant: ${matchingVariant.name}:${matchingVariant.options} with price: ${matchingVariant.price}`);
      return matchingVariant.price;
    }
  }

  // If no matching variant is found, return the base price
  console.log(`No matching variant found for ${product.name}, using base price: ${product.price}`);
  return product.price || 0;
}

/**
 * Format a variant combination into a readable string
 * @param {Object} selectedVariants - Object containing selected variant options
 * @returns {String} - Formatted string of selected variants
 */
export function formatVariantSelection(selectedVariants = {}) {
  if (!selectedVariants || Object.keys(selectedVariants).length === 0) {
    return '';
  }

  return Object.entries(selectedVariants)
    .map(([name, value]) => `${name}: ${value}`)
    .join(', ');
}

export function getTotalPrice(variants, selectedVariants) {
  let total = 0;
  for (const variant of variants) {
    total += getVariantPrice(variant, selectedVariants);
  }
  return total;
}
